import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle, ShieldCheck } from 'lucide-react';
import { EDM_UI } from '../../../../utils/edm';
import { Skeleton } from '../../../../components/common/SkeletonLoader';
import type { BrandingState } from '../../../../hooks/useLeadCaptureOnboarding';
import type { EdmBrokerOption } from './BrokerPicker';
import type { EdmSenderCandidate } from '../../../../hooks/useEdmCampaigns';

/** The founder-set company record fields eDM uses (companies/{c}). */
export interface EdmCompanyIdentity {
  name?: string | null;
  legalName?: string | null;
  abn?: string | null;
}

const present = (v: string | null | undefined): v is string => typeof v === 'string' && v.trim().length > 0;
const digits = (v: string | null | undefined): string => (v ?? '').replace(/\D/g, '');

/**
 * Mirrors the server's sender-identity rule: branding company name, address
 * and reply-to, plus the ABN on the company record (founder-set). The
 * branding ABN is not required; it only blocks when set and different.
 */
export function senderIdentityComplete(
  b: BrandingState | null | undefined,
  company: EdmCompanyIdentity | null | undefined,
): boolean {
  return (
    !!b &&
    present(b.companyName) &&
    present(b.address) &&
    present(b.replyToEmail) &&
    present(company?.abn) &&
    !abnMismatch(b, company)
  );
}

/** True when the branding ABN is set and differs from the company record's. */
export function abnMismatch(b: BrandingState | null | undefined, company: EdmCompanyIdentity | null | undefined): boolean {
  const brandingAbn = digits(b?.abn);
  return brandingAbn.length > 0 && present(company?.abn) && brandingAbn !== digits(company?.abn);
}

const Row: React.FC<{ label: string; value: string | null | undefined; display?: string }> = ({ label, value, display }) => (
  <div className="flex items-start justify-between gap-4 border-b border-gray-800 py-2 text-sm last:border-b-0">
    <span className="text-gray-400">{label}</span>
    {present(value) ? (
      <span className="flex items-center gap-2 text-right text-gray-100">
        <span className="break-all">{display ?? value}</span>
        <CheckCircle className="h-4 w-4 flex-shrink-0 text-emerald-400" aria-label="present" />
      </span>
    ) : (
      <span className="flex items-center gap-1 text-amber-300">
        <AlertTriangle className="h-4 w-4" /> missing
      </span>
    )}
  </div>
);

/**
 * Summary of the sender identity eDM uses: the Lead Capture branding (Setup
 * Wizard) plus the legal name and ABN on the company record, not the
 * Discharged Re-Engagement sender details. The only editable field is "Send
 * as" (the company, or a broker: From shows "<broker> at <company>" and
 * replies go to the broker's own email, resolved by the server). It is
 * persisted with the draft via update (sendAsBrokerUid) and locked once the
 * campaign is not a draft.
 *
 * The options come from edmCampaignAction `listSenderCandidates` (the
 * members the server will accept as a sender), not the broker roster. They
 * load on mount for a saved draft (`loadOnMount`), otherwise lazily the
 * first time the select is focused or opened, so a new campaign makes no
 * edmCampaignAction call on arrival. `brokers` is only a display-name
 * fallback for a chosen sender (e.g. a locked campaign, which never loads).
 */
const SenderIdentityPanel: React.FC<{
  branding: BrandingState | null | undefined;
  company: EdmCompanyIdentity | null | undefined;
  /** Set by the server at freeze; before that the domain is server-side. */
  fromDomain?: string | null;
  /** null = send as the company. */
  sendAsBrokerUid?: string | null;
  onSendAsChange?: (uid: string | null) => void;
  /** Display-name fallback only; the options come from loadCandidates. */
  brokers?: EdmBrokerOption[];
  /** edmCampaignAction listSenderCandidates, bound to the company. */
  loadCandidates?: () => Promise<{ candidates: EdmSenderCandidate[] }>;
  /** Load the candidates on mount (a saved draft); else on first focus/open. */
  loadOnMount?: boolean;
  /** Locked (read-only) when the campaign is not a draft. */
  disabled?: boolean;
  /** Branding is still loading: show placeholders, not "missing". */
  loading?: boolean;
}> = ({
  branding,
  company,
  fromDomain,
  sendAsBrokerUid = null,
  onSendAsChange,
  brokers = [],
  loadCandidates,
  loadOnMount = false,
  disabled,
  loading = false,
}) => {
  const [candidates, setCandidates] = useState<EdmSenderCandidate[] | null>(null);
  const requestedRef = useRef(false);
  const locked = disabled || !onSendAsChange;
  const ensureCandidates = useCallback(() => {
    if (requestedRef.current || locked || !loadCandidates) return;
    requestedRef.current = true;
    loadCandidates()
      .then((res) => {
        const list = (res?.candidates ?? [])
          .filter((c) => typeof c?.uid === 'string' && c.uid.length > 0)
          .map((c) => ({ uid: c.uid, displayName: c.displayName?.trim() || c.uid }));
        list.sort((a, b) => a.displayName.localeCompare(b.displayName));
        setCandidates(list);
      })
      .catch((err) => {
        // Non-fatal: the select keeps "Your company" and any saved sender.
        console.error('[EDM] listSenderCandidates failed (non-fatal)', err);
        setCandidates([]);
      });
  }, [locked, loadCandidates]);
  useEffect(() => {
    if (loadOnMount) ensureCandidates();
  }, [loadOnMount, ensureCandidates]);

  const brandingMissing = !present(branding?.companyName) || !present(branding?.address) || !present(branding?.replyToEmail);
  const abnMissing = !present(company?.abn);
  const mismatch = abnMismatch(branding, company);
  const fromAddress = fromDomain ? `no-reply@${fromDomain}` : "VALUTTEN's eDM sending address";
  const legalName = present(company?.legalName) ? company?.legalName : company?.name;
  const options = candidates ?? [];
  const sendAsCandidate = sendAsBrokerUid ? options.find((c) => c.uid === sendAsBrokerUid) ?? null : null;
  const sendAsBroker = sendAsBrokerUid ? brokers.find((b) => b.id === sendAsBrokerUid) ?? null : null;
  const sendAsName = sendAsBrokerUid
    ? sendAsCandidate?.displayName ?? sendAsBroker?.displayName ?? 'The selected broker'
    : null;
  const fromName = sendAsName ? `${sendAsName} at ${branding?.companyName ?? ''}` : branding?.companyName ?? '';
  return (
    <div className={EDM_UI.card}>
      <h2 className={EDM_UI.heading}>
        <ShieldCheck className={EDM_UI.headingIcon} /> Sender identity
      </h2>
      <div>
        <label htmlFor="edm-send-as" className={EDM_UI.label}>
          Send as
        </label>
        <select
          id="edm-send-as"
          className={EDM_UI.input}
          value={sendAsBrokerUid ?? ''}
          disabled={locked}
          onFocus={ensureCandidates}
          onMouseDown={ensureCandidates}
          onChange={(e) => onSendAsChange?.(e.target.value ? e.target.value : null)}
        >
          <option value="">Your company</option>
          {sendAsBrokerUid && !sendAsCandidate && (
            <option value={sendAsBrokerUid}>
              {candidates === null ? sendAsName : `${sendAsName} (not in roster)`}
            </option>
          )}
          {options.map((c) => (
            <option key={c.uid} value={c.uid}>
              {c.displayName}
            </option>
          ))}
        </select>
      </div>
      {loading ? (
        <div className="space-y-3 py-2" aria-busy="true" aria-label="Loading sender details">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-5 w-full" />
          ))}
        </div>
      ) : (
      <div>
        <Row label="From" value={branding?.companyName} display={`"${fromName}" <${fromAddress}>`} />
        {sendAsName ? (
          <Row label="Reply-to" value={branding?.replyToEmail} display={`${sendAsName}'s email address`} />
        ) : (
          <Row label="Reply-to" value={branding?.replyToEmail} />
        )}
        <div className="flex items-start justify-between gap-4 border-b border-gray-800 py-2 text-sm">
          <span className="text-gray-400">From domain</span>
          <span className="flex flex-col items-end gap-1 text-right">
            <span className="break-all text-gray-100">{fromDomain ?? 'Set when you freeze the list'}</span>
            <Link to="/leads/edm/domain" className="text-xs text-blue-300 underline hover:text-blue-200">
              Sending domain
            </Link>
          </span>
        </div>
        <Row label="Legal name (from your company record)" value={legalName} />
        <Row label="ABN (from your company record)" value={company?.abn} />
        <Row label="Address" value={branding?.address} />
      </div>
      )}
      {!loading && brandingMissing && (
        <div className={`${EDM_UI.warnBanner} flex gap-2`}>
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            Complete these in Setup Wizard › Branding before you can schedule.{' '}
            <Link to="/leads/setup" className="underline hover:text-amber-200">
              Open Setup Wizard
            </Link>
          </span>
        </div>
      )}
      {abnMissing && (
        <div className={`${EDM_UI.warnBanner} flex gap-2`}>
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>VALUTTEN holds your ABN on your company record. Contact VALUTTEN support to add it before you can schedule.</span>
        </div>
      )}
      {mismatch && (
        <div className={`${EDM_UI.warnBanner} flex gap-2`}>
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            The ABN in Setup Wizard › Branding ({branding?.abn}) does not match the ABN on your company record. Correct or
            clear it before you can schedule.
          </span>
        </div>
      )}
      <p className="text-xs text-gray-400">
        eDM uses your Lead Capture branding, not the Discharged Re-Engagement sender details.
      </p>
    </div>
  );
};

export default SenderIdentityPanel;
